import { useState, useEffect, useRef, useCallback } from "react";
import { Send, Check, CheckCheck, Bell, BellOff, X, MessageCircle, MoreVertical, Edit3, Trash2, Flag, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { bountyApi } from "@/services/api";
import { armadillo } from "@/services/security";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { BountyMessage } from "@/types";

interface BountyChatProps {
  bountyId: string;
  currentUserId: string;
  receiverId: string;
  receiverName?: string;
}

interface Notification {
  id: string;
  messageId: string;
  content: string;
  timestamp: string;
  senderName: string;
}

export const BountyChat = ({ bountyId, currentUserId, receiverId, receiverName = "Chat" }: BountyChatProps) => {
  const [messages, setMessages] = useState<BountyMessage[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [showNotifications, setShowNotifications] = useState(true);
  const [editingMessage, setEditingMessage] = useState<string | null>(null);
  const [editContent, setEditContent] = useState("");
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportingMessageId, setReportingMessageId] = useState<string | null>(null);
  const [reportReason, setReportReason] = useState("");
  const [actionMenu, setActionMenu] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [disappearingMode, setDisappearingMode] = useState(false);
  const [disappearingTimer, setDisappearingTimer] = useState<number>(0);
  const [showDisappearingMenu, setShowDisappearingMenu] = useState(false);
  const [receiverOnline, setReceiverOnline] = useState(false);
  const [receiverLastSeen, setReceiverLastSeen] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const chatHeaderRef = useRef<HTMLDivElement>(null);
  const actionMenuRef = useRef<HTMLDivElement>(null);
  const timerOptions = [
    { label: 'Off', value: 0 },
    { label: '30 seconds', value: 30 },
    { label: '1 minute', value: 60 },
    { label: '5 minutes', value: 300 },
    { label: '1 hour', value: 3600 },
  ];

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      
      // Close action menu if clicked outside (check if click was on action menu button or inside action menu)
      if (actionMenu && !target.closest(`[data-message-id="${actionMenu}"]`)) {
        setActionMenu(null);
      }
      
      // Close disappearing menu if clicked outside
      if (showDisappearingMenu && !target.closest('.disappearing-menu') && !target.closest('.disappearing-timer-btn')) {
        setShowDisappearingMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [actionMenu, showDisappearingMenu]);

  // Track online status via Supabase Realtime presence
  useEffect(() => {
    if (!receiverId) return;

    // Check receiver's online status from profiles table
    const checkOnlineStatus = async () => {
      try {
        const { data } = await supabase
          .from('profiles')
          .select('last_seen, is_online')
          .eq('id', receiverId)
          .single();
        
        if (data) {
          setReceiverOnline(data.is_online || false);
          setReceiverLastSeen(data.last_seen || null);
        }
      } catch (err) {
        console.log('Could not fetch online status');
      }
    };

    checkOnlineStatus();
    
    // Poll every 30 seconds
    const interval = setInterval(checkOnlineStatus, 30000);
    return () => clearInterval(interval);
  }, [receiverId]);

  // Load message history
  useEffect(() => {
    loadMessages();
  }, [bountyId]);

  // Subscribe to real-time messages
  useEffect(() => {
    const unsubscribe = bountyApi.subscribeToBountyMessages(bountyId, currentUserId, () => {
      loadMessages();
    });

    return () => unsubscribe();
  }, [bountyId, currentUserId]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const loadMessages = async () => {
    const { data, error } = await bountyApi.getBountyMessages(bountyId, currentUserId);
    if (!error && data) {
      const previousCount = messages.length;
      setMessages(data);
      
      // Create notification for new messages (if any)
      if (data.length > previousCount) {
        const newMsg = data[data.length - 1];
        if (newMsg.receiver_id === currentUserId && !newMsg.is_read) {
          const senderName = (newMsg as any).sender_name || "Someone";
          addNotification(newMsg.id, newMsg.content, newMsg.created_at, senderName);
        }
      }
      
      // Mark messages as read
      data.forEach((msg) => {
        if (msg.receiver_id === currentUserId && !msg.is_read) {
          bountyApi.markMessageAsRead(msg.id);
        }
      });
    }
  };

  const addNotification = (messageId: string, content: string, timestamp: string, senderName: string) => {
    if (!showNotifications) return;
    
    const notification: Notification = {
      id: `notif-${Date.now()}`,
      messageId,
      content: content.substring(0, 50) + (content.length > 50 ? '...' : ''),
      timestamp,
      senderName
    };
    
    setNotifications(prev => [notification, ...prev]);
    
    // Auto-hide notification after 10 seconds
    setTimeout(() => {
      removeNotification(notification.id);
    }, 10000);
  };

  const removeNotification = (notificationId: string) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  };

  const handleNotificationClick = (messageId: string, notificationId: string) => {
    // Scroll to the specific message
    const messageElement = document.getElementById(`message-${messageId}`);
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: "smooth", block: "center" });
      messageElement.classList.add('message-highlight');
      setTimeout(() => {
        messageElement.classList.remove('message-highlight');
      }, 2000);
    }
    
    // Remove the notification
    removeNotification(notificationId);
  };

  const handleSend = async () => {
    if (!newMessage.trim() || sending) return;

    if (!receiverId) {
      toast.error("Cannot send message: No recipient selected");
      console.error('❌ No receiverId provided');
      return;
    }

    console.log('💬 Sending message:', { bountyId, receiverId, message: newMessage.trim() });

    setSending(true);
    const messageText = newMessage.trim();
    
    // Optimistic update: Add message to UI immediately
    const optimisticMsg: any = {
      id: `optimistic-${Date.now()}`,
      bounty_id: bountyId,
      sender_id: currentUserId,
      receiver_id: receiverId,
      content: messageText,
      is_read: false,
      created_at: new Date().toISOString(),
      sender_name: 'You',
    };
    
    setMessages(prev => [...prev, optimisticMsg]);
    setNewMessage("");

    const { data, error } = await bountyApi.sendBountyMessage(bountyId, receiverId, messageText);

    if (error) {
      console.error('❌ Failed to send message:', error);
      toast.error(`Failed to send: ${error.message || 'Unknown error'}`);
      
      // Remove optimistic message on failure
      setMessages(prev => prev.filter(m => m.id !== optimisticMsg.id));
    } else {
      console.log('✅ Message sent successfully, reloading...');
      // Reload messages to get real data with proper IDs
      await loadMessages();
    }

    setSending(false);
  };

  const handleDeleteMessage = async (messageId: string) => {
    try {
      await bountyApi.deleteMessage(messageId);
      setMessages(prev => prev.filter(m => m.id !== messageId));
      toast.success('Message deleted');
      setConfirmDelete(null);
    } catch (err: any) {
      toast.error('Failed to delete message: ' + (err.message || 'Unknown error'));
    }
  };

  const handleEditMessage = async (messageId: string) => {
    if (!editContent.trim()) {
      toast.error('Message cannot be empty');
      return;
    }
    try {
      await bountyApi.updateMessage(messageId, editContent.trim());
      await loadMessages();
      setEditingMessage(null);
      setEditContent('');
      toast.success('Message updated');
    } catch (err: any) {
      toast.error('Failed to update message: ' + (err.message || 'Unknown error'));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) {
      return "Today";
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString([], { month: "short", day: "numeric" });
    }
  };

  const formatLastSeen = (lastSeen: string | null) => {
    if (!lastSeen) return 'Offline';
    const date = new Date(lastSeen);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString([], { month: "short", day: "numeric" });
  };

  return (
    <div className="flex flex-col h-[500px] border-2 border-[hsl(var(--paper-edge))] rounded-xl bg-gradient-to-br from-[hsl(var(--paper))] to-[hsl(var(--muted))] overflow-hidden shadow-lg">
      {/* Notifications Panel */}
      {notifications.length > 0 && (
        <div className="absolute top-20 right-4 z-50 w-72 space-y-2">
          {notifications.map((notif) => (
            <div
              key={notif.id}
              onClick={() => handleNotificationClick(notif.messageId, notif.id)}
              className="bg-gradient-to-r from-primary/95 to-primary/85 text-primary-foreground p-3 rounded-lg shadow-xl cursor-pointer hover:from-primary hover:to-primary/90 transition-all transform hover:scale-105 animate-in slide-in-from-right"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold mb-1">{notif.senderName}</p>
                  <p className="text-xs opacity-90 truncate">{notif.content}</p>
                  <p className="text-[10px] opacity-75 mt-1">{formatTime(notif.timestamp)}</p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeNotification(notif.id);
                  }}
                  className="p-1 hover:bg-white/20 rounded transition-colors"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-primary/10 to-primary/5 border-b-2 border-[hsl(var(--paper-edge))] p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
              <MessageCircle className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h4 className="font-bold text-sm">{receiverName}</h4>
              <div className="flex items-center gap-2">
                <span className={`inline-block w-2 h-2 rounded-full ${receiverOnline ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
                <p className="text-xs text-muted-foreground">
                  {receiverOnline ? 'Online' : `Last seen ${formatLastSeen(receiverLastSeen)}`}
                </p>
              </div>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowNotifications(!showNotifications)}
            className="h-8 w-8 p-0"
          >
            {showNotifications ? (
              <Bell className="h-4 w-4" />
            ) : (
              <BellOff className="h-4 w-4" />
            )}
          </Button>
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowDisappearingMenu(!showDisappearingMenu)}
              className={`disappearing-timer-btn h-8 w-8 p-0 ${disappearingMode ? 'text-primary' : ''}`}
              title={disappearingMode ? `Disappearing: ${timerOptions.find(t => t.value === disappearingTimer)?.label}` : 'Disappearing messages'}
            >
              <Clock className="h-4 w-4" />
            </Button>
            {showDisappearingMenu && (
              <div className="disappearing-menu absolute right-0 mt-2 bg-popover border rounded-lg shadow-lg py-2 z-50 min-w-[150px]">
                <div className="px-3 py-1 text-xs text-muted-foreground border-b mb-1">
                  Disappearing Messages
                </div>
                {timerOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => { setDisappearingTimer(opt.value); setDisappearingMode(opt.value > 0); setShowDisappearingMenu(false); }}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-accent flex items-center justify-between ${disappearingTimer === opt.value ? 'text-primary font-medium' : ''}`}
                  >
                    {opt.label}
                    {disappearingTimer === opt.value && <Check className="h-4 w-4" />}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <div ref={chatContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4 bg-[hsl(var(--background))]">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
            <MessageCircle className="h-12 w-12 mb-3 opacity-20" />
            <p className="text-sm">No messages yet</p>
            <p className="text-xs mt-1">Start the conversation!</p>
          </div>
        ) : (
          <>
            {messages.map((msg, index) => {
              const isMine = msg.sender_id === currentUserId;
              const showDate = index === 0 || formatDate(msg.created_at) !== formatDate(messages[index - 1]?.created_at);
              
              return (
                <div key={msg.id}>
                  {showDate && (
                    <div className="flex items-center justify-center my-4">
                      <div className="bg-[hsl(var(--muted))] text-xs text-muted-foreground px-3 py-1 rounded-full">
                        {formatDate(msg.created_at)}
                      </div>
                    </div>
                  )}
                  <div
                    id={`message-${msg.id}`}
                    data-message-id={msg.id}
                    className={`flex ${isMine ? "justify-end" : "justify-start"} transition-all relative`}
                  >
                    <div
                      className={`relative group max-w-[75%] rounded-2xl px-4 py-3 shadow-sm ${
                        isMine
                          ? "bg-gradient-to-br from-primary to-primary/90 text-primary-foreground rounded-br-md"
                          : "bg-[hsl(var(--paper))] border border-[hsl(var(--paper-edge))] rounded-bl-md"
                      }`}
                    >
                      {editingMessage === msg.id ? (
                        <div className="space-y-2">
                          <Textarea
                            value={editContent}
                            onChange={(e) => setEditContent(e.target.value)}
                            className="min-h-[60px] resize-none"
                            autoFocus
                          />
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => handleEditMessage(msg.id)}>
                              Save
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => { setEditingMessage(null); setEditContent(''); }}>
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="text-sm whitespace-pre-wrap break-words leading-relaxed">{msg.content}</div>
                      )}
                      <div className="flex items-center justify-end gap-1.5 mt-2">
                        {isMine && (
                          <button
                            onClick={() => setActionMenu(actionMenu === msg.id ? null : msg.id)}
                            className="p-1 hover:bg-white/20 rounded transition-colors"
                          >
                            <MoreVertical className="h-3.5 w-3.5 opacity-70" />
                          </button>
                        )}
                        {actionMenu === msg.id && (
                          <div className="absolute z-50 mt-6 right-0 bg-popover border rounded-lg shadow-lg py-1 min-w-[120px]">
                            <button
                              onClick={() => { setEditingMessage(msg.id); setEditContent(msg.content); setActionMenu(null); }}
                              className="w-full px-3 py-2 text-left text-sm hover:bg-accent flex items-center gap-2"
                            >
                              <Edit3 className="h-4 w-4" /> Edit
                            </button>
                            <button
                              onClick={() => setConfirmDelete(msg.id)}
                              className="w-full px-3 py-2 text-left text-sm hover:bg-accent text-red-500 flex items-center gap-2"
                            >
                              <Trash2 className="h-4 w-4" /> Delete
                            </button>
                            {!isMine && (
                              <button
                                onClick={() => { setReportingMessageId(msg.id); setShowReportModal(true); setActionMenu(null); }}
                                className="w-full px-3 py-2 text-left text-sm hover:bg-accent text-orange-500 flex items-center gap-2"
                              >
                                <Flag className="h-4 w-4" /> Report
                              </button>
                            )}
                          </div>
                        )}
                        <span className={`text-xs ${isMine ? 'opacity-70' : 'text-muted-foreground'}`}>
                          {formatTime(msg.created_at)}
                        </span>
                        {isMine && (
                          msg.is_read ? (
                            <CheckCheck className="h-3.5 w-3.5 opacity-70" />
                          ) : (
                            <Check className="h-3.5 w-3.5 opacity-70" />
                          )
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="border-t-2 border-[hsl(var(--paper-edge))] p-4 bg-[hsl(var(--paper))]">
        <div className="flex gap-3">
          <Textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type your message..."
            className="min-h-[44px] max-h-[120px] resize-none rounded-xl"
            rows={1}
          />
          <Button
            onClick={handleSend}
            disabled={!newMessage.trim() || sending}
            size="icon"
            className="shrink-0 h-11 w-11 rounded-xl bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 shadow-md transition-all hover:scale-105 disabled:hover:scale-100"
          >
            <Send className="h-5 w-5" />
          </Button>
        </div>
        <p className="text-[10px] text-muted-foreground mt-2 text-center">
          Press Enter to send • Shift+Enter for new line
        </p>
      </div>

      {/* Delete Confirmation Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-popover border rounded-xl p-6 max-w-sm mx-4 shadow-2xl">
            <h3 className="font-bold text-lg mb-2">Delete Message?</h3>
            <p className="text-sm text-muted-foreground mb-4">
              This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <Button variant="ghost" onClick={() => setConfirmDelete(null)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={() => handleDeleteMessage(confirmDelete)}>
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Report Message Modal */}
      {showReportModal && reportingMessageId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-popover border rounded-xl p-6 max-w-sm mx-4 shadow-2xl">
            <h3 className="font-bold text-lg mb-2 flex items-center gap-2">
              <Flag className="h-5 w-5 text-orange-500" />
              Report Message
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              This will create a dispute for admin review.
            </p>
            <div className="space-y-3 mb-4">
              <label className="text-sm font-medium">Reason for report:</label>
              <select
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
                className="w-full p-2 border rounded-lg bg-background"
                autoFocus
              >
                <option value="">Select a reason...</option>
                <option value="spam">Spam or misleading</option>
                <option value="harassment">Harassment or abuse</option>
                <option value="inappropriate">Inappropriate content</option>
                <option value="scam">Scam or fraud</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="ghost" onClick={() => { setShowReportModal(false); setReportReason(""); setReportingMessageId(null); }}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                disabled={!reportReason.trim()}
                onClick={async () => {
                  try {
                    const { error } = await bountyApi.reportBountyMessage(reportingMessageId, reportReason);
                    if (error) throw error;
                    toast.success('Message reported. Admin will review.');
                    setShowReportModal(false);
                    setReportReason("");
                    setReportingMessageId(null);
                  } catch (err: any) {
                    toast.error('Failed to report: ' + (err.message || 'Unknown error'));
                  }
                }}
              >
                Submit Report
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
